import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactElement } from "react";
import { ConfirmDialog, ContentHeader, EmptyState, QueryStatus, StatusBadge, useToast } from "@cx/ui";
import type { LogisticsIntegrationView, LogisticsOverview } from "@cx/contracts";
import { LOGISTICS_PROVIDER_IDS, MAX_LOGISTICS_INTEGRATIONS, type LogisticsProvider } from "@cx/domain";
import { can, type Permissions } from "@cx/db";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";
import { dateTimeBR } from "../invoices/InvoiceEvents";
import { IntegrationDialog } from "./LogisticsDialogs";
import { INTEGRATION_STATUS_LABEL, INTEGRATION_STATUS_TONE, kgLabel, providerLabel } from "./labels";

const PROVIDER_HINT: Record<LogisticsProvider, string> = {
  correios:
    "Contrato com os Correios (API CWS): etiquetas, pré-postagem, remessas com lista de postagem e rastreio.",
  melhorenvio:
    "Conta do Melhor Envio (autorização OAuth): várias transportadoras, etiqueta paga com o saldo da carteira e rastreio.",
};
const MODE_LABEL = { sandbox: "Ambiente de testes", live: "Produção", disabled: "Indisponível" } as const;

/**
 * Preferências › Integrações › Logística (T3.4, RF-VEN-10): conectar Correios (credenciais do CWS, cifradas) e Melhor
 * Envio (OAuth), configurar serviços, pacote padrão e validade da reversa, desativar (os objetos ficam).
 */
export function LogisticsSettingsPage(): ReactElement {
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const qc = useQueryClient();
  const toast = useToast();
  const key = ["logistics", companyId, "overview"];
  const overview = useQuery({ queryKey: key, queryFn: () => api<LogisticsOverview>("/logistics/overview") });
  const [editing, setEditing] = useState<LogisticsIntegrationView | null | undefined>(undefined);
  const [deactivating, setDeactivating] = useState<LogisticsIntegrationView | null>(null);
  const refresh = () => void qc.invalidateQueries({ queryKey: ["logistics", companyId] });
  const onError = (e: Error) => toast("danger", errorMessage(e));
  const authorize = useMutation({
    mutationFn: () =>
      api<{ url: string }>("/logistics/integrations/melhorenvio/authorize", { method: "POST", body: "{}" }),
    onSuccess: (r) => window.location.assign(r.url),
    onError,
  });
  const deactivate = useMutation({
    mutationFn: (id: string) =>
      api<LogisticsIntegrationView>(`/logistics/integrations/${id}/deactivate`, {
        method: "POST",
        body: "{}",
      }),
    onSuccess: () => {
      toast("success", "Integração desativada");
      refresh();
    },
    onError,
  });
  const o = overview.data;
  const canCreate = can(perms, "logistics", "create");
  const canUpdate = can(perms, "logistics", "update");
  const full = (p: LogisticsProvider) =>
    (o?.integrations.filter((i) => i.provider === p && i.status !== "inactive").length ?? 0) >=
    MAX_LOGISTICS_INTEGRATIONS;
  return (
    <>
      <ContentHeader title="Logística" breadcrumb={["Preferências", "Integrações", "Logística"]} />
      <QueryStatus loading={overview.isLoading} error={overview.error} />
      {o && (
        <>
          <div className="row">
            {LOGISTICS_PROVIDER_IDS.map((p) => {
              const mode = o.modes[p];
              return (
                <div className="col-md-6" key={p}>
                  <div className="box card p-3 mb-3">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <h2 className="h6 mb-0">{providerLabel(p)}</h2>
                      {mode === "sandbox" && (
                        <span className="badge text-bg-warning">{MODE_LABEL[mode]}</span>
                      )}
                    </div>
                    <p className="small text-muted">{PROVIDER_HINT[p]}</p>
                    {mode === "disabled" ? (
                      <p className="small mb-0">Integração indisponível neste ambiente.</p>
                    ) : (
                      canCreate && (
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          disabled={full(p) || (p === "melhorenvio" && authorize.isPending)}
                          title={
                            full(p)
                              ? `Até ${MAX_LOGISTICS_INTEGRATIONS} integrações ativas por provedor`
                              : undefined
                          }
                          onClick={() => (p === "correios" ? setEditing(null) : authorize.mutate())}
                        >
                          Conectar {providerLabel(p)}
                        </button>
                      )
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="box card">
            {o.integrations.length === 0 ? (
              <EmptyState
                title="Nenhuma integração de logística"
                description="Conecte os Correios ou o Melhor Envio para gerar etiquetas a partir dos pedidos de venda."
                actions={[]}
              />
            ) : (
              <div className="table-responsive">
                <table className="table align-middle mb-0" aria-label="Integrações de logística">
                  <thead>
                    <tr>
                      <th scope="col">Integração</th>
                      <th scope="col">Conta</th>
                      <th scope="col">Pacote padrão</th>
                      <th scope="col">Último rastreio</th>
                      <th scope="col">Situação</th>
                      <th scope="col">
                        <span className="visually-hidden">Ações</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {o.integrations.map((i) => (
                      <tr key={i.id}>
                        <td>
                          <strong>{i.name}</strong>
                          <div className="small text-muted">
                            {providerLabel(i.provider)}
                            {i.sandbox ? " · testes" : ""} · {i.activeShipments}{" "}
                            {i.activeShipments === 1 ? "objeto em andamento" : "objetos em andamento"}
                          </div>
                        </td>
                        <td>{i.externalName ?? "—"}</td>
                        <td>
                          {i.settings.defaultPackage
                            ? kgLabel(i.settings.defaultPackage.weightG)
                            : "Pelos produtos"}
                        </td>
                        <td>{i.lastTrackedAt ? dateTimeBR(i.lastTrackedAt) : "—"}</td>
                        <td>
                          <StatusBadge tone={INTEGRATION_STATUS_TONE[i.status]}>
                            {INTEGRATION_STATUS_LABEL[i.status]}
                          </StatusBadge>
                          {i.lastError && i.status !== "inactive" && (
                            <div className="small text-danger">{i.lastError}</div>
                          )}
                        </td>
                        <td className="text-end text-nowrap">
                          {canCreate && i.status === "reauth_required" && i.provider === "melhorenvio" && (
                            <button
                              type="button"
                              className="btn btn-outline-primary btn-sm me-2"
                              disabled={authorize.isPending}
                              onClick={() => authorize.mutate()}
                            >
                              Reconectar
                            </button>
                          )}
                          {canUpdate && i.status !== "inactive" && (
                            <>
                              <button
                                type="button"
                                className="btn btn-outline-secondary btn-sm me-2"
                                onClick={() => setEditing(i)}
                              >
                                Configurar
                              </button>
                              <button
                                type="button"
                                className="btn btn-outline-danger btn-sm"
                                onClick={() => setDeactivating(i)}
                              >
                                Desativar
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
      <IntegrationDialog
        open={editing !== undefined}
        integration={editing ?? null}
        onClose={() => setEditing(undefined)}
        onDone={() => {
          setEditing(undefined);
          refresh();
        }}
      />
      <ConfirmDialog
        open={!!deactivating}
        title="Desativar integração"
        message={`Desativar ${deactivating?.name ?? ""}? As credenciais são apagadas; objetos e remessas continuam no histórico, mas deixam de ser rastreados.`}
        confirmLabel="Desativar"
        tone="danger"
        onConfirm={() => {
          if (deactivating) deactivate.mutate(deactivating.id);
          setDeactivating(null);
        }}
        onCancel={() => setDeactivating(null)}
      />
    </>
  );
}
